import React, { useEffect, useState, useCallback } from "react";
import { useMyContext } from "../Context/MyContext";
import OrdersTable from "./OrdersTable";
import VisitorsTracker from "./visitorTracker";
import InventoryPage from "./InventoryPage";
import SalesPage from "./SalesPage";
import UsersPage from "./UsersPage";
import PaymentsPage from "./PaymentsPage";

import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useLocation } from "react-router-dom";

const Dashboard = () => {
  const { currentUser, orders } = useMyContext();
  const location = useLocation();

  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [showVisitorsModal, setShowVisitorsModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);

  const [totalVisitors, setTotalVisitors] = useState(0);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);

  const userName = currentUser?.name || "Guest User";
  const userRole = currentUser?.role || "Regular User";
  const userID = currentUser?.uid ? currentUser.uid.substring(0, 6) : "----";

  /* ---------------- FETCH NON-ORDER STATS ---------------- */
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const visitorsSnap = await getDocs(collection(db, "visitors"));
        setTotalVisitors(visitorsSnap.size);

        const productsSnap = await getDocs(collection(db, "dishes"));
        setTotalProducts(productsSnap.size);

        const usersSnap = await getDocs(collection(db, "users"));
        setTotalUsers(usersSnap.size);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };

    fetchStats();
  }, []);

  /* ---------------- ORDER-BASED CALCULATIONS ---------------- */

  // 🔹 Total sales (ALL orders, paid + pending)
  const totalSales = (orders || []).reduce(
    (sum, o) => sum + (Number(o.totalAmount) || 0),
    0
  );

  // ✅ Payments Received (ONLY PAID / SUCCESS)
  const totalPaymentsReceived = (orders || [])
    .filter((o) => o.payment?.status === "Paid" || o.payment?.status === "Success")
    .reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0);

  // 🔹 Customer personal transactions
  const myTransactions = (orders || [])
    .filter((o) => o.userId === currentUser?.uid)
    .reduce((sum, o) => sum + (Number(o.totalAmount) || 0), 0);

  /* ---------------- DASHBOARD CARDS ---------------- */

  const adminCards = [
    {
      title: "Total Products / Inventory",
      color: "from-[#7b2cbf] via-[#8b5cf6] to-[#45a864]",
      glow: "hover:shadow-[0_20px_45px_rgba(123,44,191,0.35)]",
      total: totalProducts,
      isInventory: true,
    },
    {
      title: "Orders / Sales",
      color: "from-[#5b21b6] via-[#7b2cbf] to-[#9333ea]",
      glow: "hover:shadow-[0_20px_45px_rgba(123,44,191,0.35)]",
      total: (orders || []).length,
      isOrders: true,
    },
    {
      title: "Visitors",
      color: "from-[#6d28d9] via-[#8b5cf6] to-[#a855f7]",
      glow: "hover:shadow-[0_20px_45px_rgba(139,92,246,0.35)]",
      total: totalVisitors,
      isVisitors: true,
    },
    {
      title: "Payments Received (₦)",
      color: "from-[#2f855a] via-[#45a864] to-[#68c989]",
      glow: "hover:shadow-[0_20px_45px_rgba(69,168,100,0.35)]",
      total: totalPaymentsReceived,
      isPayments: true,
    },
    {
      title: "Users",
      color: "from-[#4c1d95] via-[#6d28d9] to-[#45a864]",
      glow: "hover:shadow-[0_20px_45px_rgba(76,29,149,0.35)]",
      total: totalUsers,
      isUsers: true,
    },
  ];

  const customerCards = [
    {
      title: "My Orders",
      color: "from-[#5b21b6] via-[#7b2cbf] to-[#9333ea]",
      glow: "hover:shadow-[0_20px_45px_rgba(123,44,191,0.35)]",
      total: (orders || []).filter((o) => o.userId === currentUser?.uid).length,
      isOrders: true,
    },
    {
      title: "My Transactions (₦)",
      color: "from-[#2f855a] via-[#45a864] to-[#68c989]",
      glow: "hover:shadow-[0_20px_45px_rgba(69,168,100,0.35)]",
      total: myTransactions,
      isOrders: true,
    },
  ];

  // ✅ Close everything (memoized so effects can use it safely)
  const closeAllModals = useCallback(() => {
    setShowOrdersModal(false);
    setShowVisitorsModal(false);
    setShowInventoryModal(false);
    setShowSalesModal(false);
    setShowUsersModal(false);
    setShowPaymentsModal(false);

    try {
      document.body.style.overflow = "";
    } catch {}
  }, []);

  const isAnyModalOpen =
    showOrdersModal ||
    showVisitorsModal ||
    showInventoryModal ||
    showSalesModal ||
    showUsersModal ||
    showPaymentsModal;

  // ✅ LOCK BACKGROUND SCROLL WHEN ANY MODAL IS OPEN
  useEffect(() => {
    const prev = document.body.style.overflow;
    if (isAnyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = prev || "";
    };
  }, [isAnyModalOpen]);

  // ✅ IMPORTANT: Listen for the global event dispatched from OrdersTable
  // This lets OrdersTable close the Dashboard modal shell reliably.
  useEffect(() => {
    const handler = () => closeAllModals();
    window.addEventListener("close-all-dashboard-modals", handler);
    return () => window.removeEventListener("close-all-dashboard-modals", handler);
  }, [closeAllModals]);

  // ✅ Close all modals when route changes
  useEffect(() => {
    closeAllModals();
  }, [location.pathname, location.search, closeAllModals]);

  const handleCardClick = (card) => {
    // ✅ ensure only ONE modal opens
    closeAllModals();

    // ✅ open the selected modal next tick (avoids overlap/race on some browsers)
    setTimeout(() => {
      if (card.isOrders) setShowOrdersModal(true);
      else if (card.isVisitors) setShowVisitorsModal(true);
      else if (card.isInventory) setShowInventoryModal(true);
      else if (card.isSales) setShowSalesModal(true);
      else if (card.isUsers) setShowUsersModal(true);
      else if (card.isPayments) setShowPaymentsModal(true);
    }, 0);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-[#f7f0ff] via-white to-[#eefbf3] p-4 sm:p-6 md:p-12">
      <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[#7b2cbf]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[#45a864]/10 blur-3xl" />

      {/* ✅ When a card is selected, hide dashboard content */}
      {!isAnyModalOpen && (
        <div className="relative z-10">
          {/* DASHBOARD HEADING */}
          <div className="mb-8 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-[#45a864]">
              CHAREX
            </p>

            <h1 className="mt-2 text-3xl font-extrabold text-[#4c1d95] sm:text-4xl">
              Dashboard
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Shop Smart. Save More. Smile More.
            </p>
          </div>

          {/* PROFILE */}
          <div className="relative mx-auto mb-10 w-full max-w-md overflow-hidden rounded-3xl border border-[#e9d5ff] bg-white shadow-[0_18px_50px_rgba(76,29,149,0.14)]">
            <div className="h-24 bg-gradient-to-r from-[#4c1d95] via-[#7b2cbf] to-[#45a864]" />

            <div className="relative flex flex-col items-center px-6 pb-7">
              <div className="-mt-10 flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-[#7b2cbf] to-[#45a864] text-2xl font-extrabold text-white shadow-lg">
                {userName.charAt(0).toUpperCase()}
              </div>

              <div className="mt-4 text-center">
                <p className="mb-1 text-xl font-bold text-[#4c1d95]">
                  {userName}
                </p>

                <span className="inline-flex rounded-full bg-[#f3e8ff] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[#7b2cbf]">
                  {userRole}
                </span>

                <p className="mt-3 text-sm text-gray-400">
                  User ID: {userID}...
                </p>
              </div>
            </div>
          </div>

          {/* SUMMARY STRIP */}
          <div className="mx-auto mb-8 grid max-w-5xl grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-2xl border border-[#e9d5ff] bg-white/90 p-4 text-center shadow-sm backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Total Sales
              </p>
              <p className="mt-1 text-xl font-extrabold text-[#7b2cbf]">
                ₦{Number(totalSales || 0).toLocaleString()}
              </p>
            </div>

            <div className="rounded-2xl border border-[#d7f0df] bg-white/90 p-4 text-center shadow-sm backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Payments Received
              </p>
              <p className="mt-1 text-xl font-extrabold text-[#45a864]">
                ₦{Number(totalPaymentsReceived || 0).toLocaleString()}
              </p>
            </div>

            <div className="rounded-2xl border border-[#e9d5ff] bg-white/90 p-4 text-center shadow-sm backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Active Role
              </p>
              <p className="mt-1 text-xl font-extrabold capitalize text-[#4c1d95]">
                {userRole}
              </p>
            </div>
          </div>

          {/* CARDS */}
          <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {(userRole === "admin" ? adminCards : customerCards).map((card) => (
              <div
                key={card.title}
                role="button"
                tabIndex={0}
                onClick={() => handleCardClick(card)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleCardClick(card);
                }}
                className={`group relative min-h-[170px] cursor-pointer overflow-hidden rounded-3xl bg-gradient-to-br ${card.color} p-6 text-white shadow-lg transition-all duration-300 hover:-translate-y-2 hover:scale-[1.03] ${card.glow} focus:outline-none focus:ring-4 focus:ring-[#7b2cbf]/25`}
              >
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/10 transition-transform duration-500 group-hover:scale-125" />
                <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-black/10 transition-transform duration-500 group-hover:scale-125" />

                <div className="relative z-10 flex h-full flex-col items-center justify-center text-center">
                  <p className="text-base font-bold leading-snug sm:text-lg">
                    {card.title}
                  </p>

                  <div className="mt-4 h-px w-12 bg-white/60 transition-all duration-300 group-hover:w-20" />

                  <p className="mt-4 text-3xl font-extrabold drop-shadow-sm">
                    {typeof card.total === "number"
                      ? card.title.includes("(₦)") || card.title.includes("Transactions")
                        ? `₦${Number(card.total || 0).toLocaleString()}`
                        : Number(card.total || 0).toLocaleString()
                      : card.total}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODALS */}
      {showOrdersModal && (
        <Modal close={() => setShowOrdersModal(false)} title="Orders / Sales">
          <OrdersTable onClose={() => setShowOrdersModal(false)} />
        </Modal>
      )}

      {showVisitorsModal && (
        <Modal close={() => setShowVisitorsModal(false)} title="Visitors">
          <VisitorsTracker />
        </Modal>
      )}

      {showInventoryModal && (
        <Modal close={() => setShowInventoryModal(false)} title="Inventory">
          <InventoryPage />
        </Modal>
      )}

      {showSalesModal && (
        <Modal close={() => setShowSalesModal(false)} title="Sales">
          <SalesPage />
        </Modal>
      )}

      {showUsersModal && (
        <Modal close={() => setShowUsersModal(false)} title="Users">
          <UsersPage />
        </Modal>
      )}

      {showPaymentsModal && (
        <Modal close={() => setShowPaymentsModal(false)} title="Payments">
          <PaymentsPage />
        </Modal>
      )}
    </div>
  );
};

/* ---------------- REUSABLE MODAL ---------------- */
const Modal = ({ close, children, title = "CHAREX Dashboard" }) => (
  <div
    className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#1f1235]/85 p-3 backdrop-blur-sm sm:p-5"
    onClick={close} // ✅ click outside closes
    role="presentation"
  >
    <div
      className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-[#d8b4fe] bg-white shadow-[0_25px_80px_rgba(31,18,53,0.45)] sm:w-11/12"
      onClick={(e) => e.stopPropagation()} // ✅ prevent inside clicks from closing
      role="presentation"
    >
      <div className="flex min-h-[72px] items-center justify-between bg-gradient-to-r from-[#4c1d95] via-[#7b2cbf] to-[#45a864] px-5 pr-16 text-white sm:px-8">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-green-200">
            CHAREX
          </p>

          <h2 className="mt-1 text-xl font-extrabold sm:text-2xl">
            {title}
          </h2>
        </div>
      </div>

      <button
        type="button"
        onClick={close}
        className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/15 text-xl font-bold text-white transition-all duration-300 hover:rotate-90 hover:bg-white hover:text-[#7b2cbf]"
        aria-label="Close dashboard modal"
      >
        ✕
      </button>

      <div className="flex-1 overflow-y-auto bg-gradient-to-br from-[#fbf8ff] via-white to-[#f3fbf5] px-4 pb-6 pt-5 sm:px-6">
        {children}
      </div>
    </div>
  </div>
);

export default Dashboard;